import { combineReducers } from 'redux';
import { ICustomer } from '../../../data/entities/customer';
import { IFinancial } from '../../../data/entities/customer/financial';
import { IEntityActions } from '../../../data/store';
import { Actions as AuthUserActions, ActionTypes as AuthUserActionTypes } from '../../../entities/auth/user/actions';
import { Actions as ContractActions, ActionTypes as ContractActionTypes } from '../../../entities/contracts/actions';
import { APP_LOCATION_CHANGE, LocationChangeAction } from '../../../types';
import { SaveState } from '../../../types';
import { Actions, ActionTypes } from './actions';
import { SupportedPaymentTypes } from './payment/CustomerPaymentDetails';
import { IValidationResult, validateCustomer, validateFinancial } from './validation';

interface IPersonalDetailsDraft {
	[key: string]: {
		customer: ICustomer,
		touched: boolean;
	};
}

interface IFinancialDraft {
	[key: string]: {
		financial: IFinancial,
		touched: boolean;
	};
}

const hotswapAccountToIntl = (financial: IFinancial) => {
	if (financial.payment_method === SupportedPaymentTypes.NON_IBAN) {
		return { ...financial, intl_account_number: financial.account_number, account_number: '' };
	} else {
		return { ...financial, intl_account_number: '' };
	}
};

export const drafts = (state = {} as IPersonalDetailsDraft, action: IEntityActions | Actions = {} as any) => {
	switch (action.type) {
		case ActionTypes.CUSTOMER_UI_CHANGED:
			return { ...state, [action.payload.id]: { customer: action.payload.customer, touched: true } };
		case 'CUSTOMER_GET_BY_ID_RECEIVE':
		case 'CUSTOMER_CREATE_RECEIVE':
		case 'CUSTOMER_UPDATE_RECEIVE':
			return { ...state, [action.payload.id]: { customer: action.payload.customer, touched: false } };
		default:
			return state;
	}
};

export const financialDrafts = (state = {} as IFinancialDraft, action: IEntityActions | Actions = {} as any) => {
	switch (action.type) {
		case ActionTypes.CUSTOMER_FINANCIAL_UI_CHANGED:
			return { ...state, [action.payload.id]: { financial: action.payload.financial, touched: true } };
		case 'FINANCIAL_GET_BY_CUSTOMER_RECEIVE':
		case 'FINANCIAL_UPSERT_RECEIVE':
			return { ...state, [action.payload.id]: { financial: hotswapAccountToIntl(action.payload.financial), touched: false } };
		default:
			return state;
	}
};

export const loading = (state = false, action: IEntityActions | ContractActions = {} as any) => {
	switch (action.type) {
		case 'CUSTOMER_GET_BY_ID_REQUEST':
		case ContractActionTypes.CUSTOMER_CONTRACTS_REQUEST:
			return true;
		case 'CUSTOMER_GET_BY_ID_RECEIVE':
		case 'CUSTOMER_GET_BY_ID_FAILURE':
		case ContractActionTypes.CUSTOMER_CONTRACTS_RECEIVE:
		case ContractActionTypes.CUSTOMER_CONTRACTS_FAILURE:
			return false;
		default:
			return state;
	}
};

export const financialLoading = (state = false, action: IEntityActions = {} as any) => {
	switch (action.type) {
		case 'FINANCIAL_GET_BY_CUSTOMER_REQUEST':
			return true;
		case 'FINANCIAL_GET_BY_CUSTOMER_RECEIVE':
		case 'FINANCIAL_GET_BY_CUSTOMER_FAILURE':
			return false;
		default:
			return state;
	}
};

export const saving = (state = false, action: IEntityActions = {} as any) => {
	switch (action.type) {
		case 'CUSTOMER_UPDATE_REQUEST':
			return true;
		case 'CUSTOMER_UPDATE_RECEIVE':
		case 'CUSTOMER_UPDATE_FAILURE':
			return false;
		default:
			return state;
	}
};

export const financialSaving = (state = false, action: IEntityActions = {} as any) => {
	switch (action.type) {
		case 'FINANCIAL_UPSERT_REQUEST':
			return true;
		case 'FINANCIAL_UPSERT_RECEIVE':
		case 'FINANCIAL_UPSERT_FAILURE':
			return false;
		default:
			return state;
	}
};

export const saveState = (state = SaveState.NONE, action: IEntityActions | Actions | LocationChangeAction = {} as any) => {
	switch (action.type) {
		case 'CUSTOMER_UPDATE_REQUEST':
			return SaveState.PENDING;
		case 'CUSTOMER_UPDATE_RECEIVE':
			return SaveState.SUCCESS;
		case 'CUSTOMER_UPDATE_FAILURE':
			return SaveState.FAILURE;
		case ActionTypes.CUSTOMER_UI_CHANGED:
		case APP_LOCATION_CHANGE:
			return SaveState.NONE;
		default:
			return state;
	}
};

export const financialSaveState = (state = SaveState.NONE, action: IEntityActions | Actions | LocationChangeAction = {} as any) => {
	switch (action.type) {
		case 'FINANCIAL_UPSERT_REQUEST':
			return SaveState.PENDING;
		case 'FINANCIAL_UPSERT_RECEIVE':
			return SaveState.SUCCESS;
		case 'FINANCIAL_UPSERT_FAILURE':
			return SaveState.FAILURE;
		case ActionTypes.CUSTOMER_FINANCIAL_UI_CHANGED:
		case APP_LOCATION_CHANGE:
			return SaveState.NONE;
		default:
			return state;
	}
};

export const resettingPassword = (state = { state: SaveState.NONE, context: {} }, action: AuthUserActions | LocationChangeAction = {} as any) => {
	switch (action.type) {
		case AuthUserActionTypes.PASSWORD_RESET_REQUEST:
			return { state: SaveState.PENDING, context: {} };
		case AuthUserActionTypes.PASSWORD_RESET_RECEIVE:
			return { state: SaveState.SUCCESS, context: {} };
		case AuthUserActionTypes.PASSWORD_RESET_FAILURE:
			return { state: SaveState.FAILURE, context: action.payload.error };
		case APP_LOCATION_CHANGE:
			return { state: SaveState.NONE, context: {} };
		default:
			return state;
	}
};

export const validation = (state = {} as { [key: string]: IValidationResult[] }, action: IEntityActions | Actions = {} as any) => {
	switch (action.type) {
		case 'CUSTOMER_GET_BY_ID_RECEIVE':
		case 'CUSTOMER_CREATE_RECEIVE':
		case 'CUSTOMER_UPDATE_RECEIVE':
		case ActionTypes.CUSTOMER_UI_CHANGED:
			return { ...state, [action.payload.id]: validateCustomer(action.payload.customer) };
		default:
			return state;
	}
};

export const financialValidation = (state = {} as { [key: string]: IValidationResult[] }, action: IEntityActions | Actions = {} as any) => {
	switch (action.type) {
		case 'FINANCIAL_GET_BY_CUSTOMER_RECEIVE':
		case 'FINANCIAL_UPSERT_RECEIVE':
			return { ...state, [action.payload.id]: validateFinancial(hotswapAccountToIntl(action.payload.financial)) };
		case ActionTypes.CUSTOMER_FINANCIAL_UI_CHANGED:
			return { ...state, [action.payload.id]: validateFinancial(action.payload.financial) };
		default:
			return state;
	}
};

export interface ICustomerPageState {
	drafts: ReturnType<typeof drafts>;
	financialDrafts: ReturnType<typeof financialDrafts>;
	loading: ReturnType<typeof loading>;
	financialLoading: ReturnType<typeof financialLoading>;
	saving: ReturnType<typeof saving>;
	financialSaving: ReturnType<typeof financialSaving>;
	saveState: ReturnType<typeof saveState>;
	financialSaveState: ReturnType<typeof financialSaveState>;
	resettingPassword: ReturnType<typeof resettingPassword>;
	validation: ReturnType<typeof validation>;
	financialValidation: ReturnType<typeof financialValidation>;
}

export default combineReducers<ICustomerPageState>({
	drafts,
	financialDrafts,
	loading,
	financialLoading,
	saving,
	financialSaving,
	saveState,
	financialSaveState,
	resettingPassword,
	validation,
	financialValidation,
});
